/*
 * Section admin : objectif de cohorte.
 * Objectif valide par le proprietaire : 15 ventes par tranche de 90 jours.
 * Les ventes correspondent aux cartes GoHighLevel dans les etapes
 * Cliente engagee ou Accompagnement en cours. Compteurs agreges uniquement.
 */

import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Target, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { adminGetObjective } from "@/lib/api";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-5";

const formatDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const ObjectiveSection = ({ objective, onPatch, adminKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await adminGetObjective(adminKey));
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    load();
  }, [load]);

  const target = data?.target || 1;
  const ventes = data?.ventes || 0;
  const pct = Math.min(Math.round((ventes / target) * 100), 100);

  return (
    <section className={sectionClass} data-testid="admin-objective-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#235277]">
            <Target className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-heading text-base font-semibold text-[#060606]">
              Objectif de cohorte
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
            Suivi des ventes de la tranche en cours. Une vente correspond à une carte
            placée dans Cliente engagée ou Accompagnement en cours dans votre pipeline
            GoHighLevel.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-objective-refresh-button"
          className="rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-medium transition-colors duration-200"
        >
          <RefreshCw className={`mr-1.5 w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-2/3 rounded" />
          <Skeleton className="h-4 w-full rounded" />
        </div>
      ) : error ? (
        <div
          className="flex items-start gap-3 rounded-xl bg-[#FDF6E7] border border-[rgba(94,85,21,0.25)] px-4 py-3"
          data-testid="admin-objective-error"
        >
          <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-[#5E5515]" aria-hidden="true" />
          <p className="text-xs font-medium text-[#5E5515]">
            La progression n'a pas pu être chargée. Actualise pour réessayer.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="font-heading text-3xl font-semibold text-[#235277]" data-testid="admin-objective-progress-text">
                {ventes} <span className="text-lg text-[#9E9E9E]">sur {target} ventes</span>
              </p>
              <p className="mt-1 text-xs text-[#9E9E9E]">
                Tranche {data?.tranche_index} : du {formatDate(data?.window_start)} au {formatDate(data?.window_end)}
              </p>
            </div>
            <p className="text-xs font-semibold text-[#333333]" data-testid="admin-objective-days-remaining">
              {data?.days_remaining} jours restants
            </p>
          </div>
          <div className="h-3 rounded-full bg-[rgba(35,82,119,0.08)] overflow-hidden" role="progressbar"
               aria-valuenow={ventes} aria-valuemin={0} aria-valuemax={target}>
            <div
              className={`h-full rounded-full ${pct >= 100 ? "bg-[#114E0B]" : "bg-[#235277]"}`}
              style={{ width: `${Math.max(pct, ventes > 0 ? 4 : 0)}%` }}
              data-testid="admin-objective-progress-bar"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.35)] p-3" data-testid="admin-objective-bilans">
              <p className="font-heading text-lg font-semibold text-[#235277]">{data?.bilans_sent ?? 0}</p>
              <p className="text-[11px] font-medium text-[#333333]">Bilans envoyés dans la tranche</p>
            </div>
            <div className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.35)] p-3" data-testid="admin-objective-rdv">
              <p className="font-heading text-lg font-semibold text-[#235277]">{data?.appointments ?? 0}</p>
              <p className="text-[11px] font-medium text-[#333333]">Rendez vous dans la tranche</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-[rgba(35,82,119,0.1)]">
        <div>
          <Label htmlFor="objective-target" className="text-xs font-medium text-[#060606]">Objectif de ventes</Label>
          <Input
            id="objective-target"
            type="number"
            min="1"
            data-testid="admin-objective-target-input"
            value={objective.target}
            onChange={(e) => onPatch("target", e.target.value)}
            className="mt-1.5 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
        </div>
        <div>
          <Label htmlFor="objective-window" className="text-xs font-medium text-[#060606]">Durée de tranche (jours)</Label>
          <Input
            id="objective-window"
            type="number"
            min="7"
            data-testid="admin-objective-window-input"
            value={objective.windowDays}
            onChange={(e) => onPatch("windowDays", e.target.value)}
            className="mt-1.5 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
        </div>
        <div>
          <Label htmlFor="objective-start" className="text-xs font-medium text-[#060606]">Début de la première tranche</Label>
          <Input
            id="objective-start"
            type="date"
            data-testid="admin-objective-start-input"
            value={objective.startDate}
            onChange={(e) => onPatch("startDate", e.target.value)}
            className="mt-1.5 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
        </div>
      </div>
      <p className="text-[11px] leading-relaxed text-[#9E9E9E]">
        Pense à enregistrer les paramètres après modification, puis actualise la progression.
      </p>
    </section>
  );
};
